import { useState, useCallback, useEffect, useRef } from "react";
import { Layers, BookOpen, Plus, ChevronRight, Sparkles, Check, Copy, X } from "lucide-react";
import Studio from "./Hem_and_Haw_The_Studio";
import Measurements from "./Hem_and_Haw_The_Fitting_Room";
import Salon from "./Hem_and_Haw_The_Salon";
import TechPack from "./Hem_and_Haw_The_Drawing_Room";
import TheGallery from "./Hem_and_Haw_The_Gallery";
import TabithaChat from "./Hem_and_Haw_Chatbot";
import ContactPage from "./Hem_and_Haw_Contact";
import tabithaAvatar from "./Tabitha_-_Vector.png";
import {
  getOrCreateSession,
  saveDesign,
  loadSessionDesigns,
  loadDesignBySeed,
  deleteDesign,
  createShareToken,
  loadSharedDesign,
  signInWithMagicLink,
  signOut,
  onAuthStateChange,
  mergeSessionToUser,
  submitDesignToGallery,
} from "./supabaseClient";

const ANTHROPIC_KEY = process.env.REACT_APP_ANTHROPIC_API_KEY || "";

// ─────────────────────────────────────────────────────────────────────────────
// Shared design view — shown when visiting with ?share=token
// ─────────────────────────────────────────────────────────────────────────────
function SharedDesignView({ token, onStartDesign }) {
  const [design, setDesign]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(function() {
    loadSharedDesign(token).then(function(d) {
      if (!d) setError("This design link has expired or doesn't exist.");
      else setDesign(d);
    }).catch(function() {
      setError("Could not load the shared design — please try again.");
    }).finally(function() {
      setLoading(false);
    });
  }, [token]);

  const goalLabel = design ? ({ business:"Business", cocktail:"Cocktail", gala:"Gala / Black Tie", bridal:"Bridal" }[design.form.category] || design.form.category) : "";
  const fabricName = design ? (design.form.fabricId.charAt(0).toUpperCase() + design.form.fabricId.slice(1).replace("-"," ")) : "";

  return (
    <div style={{ margin:0, padding:0, minHeight:"100vh", backgroundColor:"#F7F4F1", fontFamily:"system-ui,sans-serif" }}>
      {/* Nav */}
      <div style={{ position:"sticky", top:0, zIndex:60, backgroundColor:"#FFFFFF", borderBottom:"1px solid #E0D9D3", boxShadow:"0 2px 10px rgba(0,0,0,0.07)", padding:"10px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Layers style={{ color:C, width:18, height:18 }} />
          <span style={{ fontSize:13, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif" }}>Tabitha</span>
        </div>
        <button onClick={onStartDesign} style={{ display:"flex", alignItems:"center", gap:7, padding:"7px 16px", borderRadius:999, backgroundColor:C, border:"none", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
          <Sparkles style={{ width:11, height:11, color:"#fff" }} />
          <span style={{ fontSize:10, fontWeight:700, color:"#fff", letterSpacing:"0.15em", textTransform:"uppercase" }}>Start My Design</span>
        </button>
      </div>

      {/* Pink band */}
      <div style={{ backgroundColor:"#C9A0A0", padding:"18px 28px" }}>
        <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.3em", color:"#6f555f", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>Shared Design</div>
      </div>

      <div style={{ margin:"0 auto", padding:"28px 20px 64px", maxWidth:720 }}>
        {loading && (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:400 }}>
            <div style={{ fontSize:11, color:"#a8a29e", letterSpacing:"0.2em", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>Loading design…</div>
          </div>
        )}
        {error && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:400, gap:12, textAlign:"center" }}>
            <div style={{ fontSize:15, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif" }}>{error}</div>
            <button onClick={onStartDesign} style={{ padding:"12px 24px", borderRadius:999, backgroundColor:C, color:"#fff", border:"none", cursor:"pointer", fontSize:11, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>
              Start Your Own Design
            </button>
          </div>
        )}
        {design && (
          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
            {/* Title */}
            <div>
              <div style={{ fontSize:22, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif", letterSpacing:"-0.02em" }}>{goalLabel} {design.form.cut}</div>
              <div style={{ fontSize:13, color:"#78716c", fontFamily:"system-ui,sans-serif", marginTop:4 }}>{fabricName} · {design.form.colorDescription}</div>
            </div>

            {/* Images */}
            {(design.designRender || design.fittingRoomRender) && (
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {[
                  { src: design.designRender,      label:"Studio Render" },
                  { src: design.fittingRoomRender, label:"Salon Render" },
                  { src: design.fittingRoomRenderBack, label:"Salon Back" },
                ].filter(function(v) { return !!v.src; }).map(function(view) {
                  return (
                    <div key={view.label} style={{ borderRadius:16, overflow:"hidden", backgroundColor:"#1a1714", position:"relative" }}>
                      <img src={view.src} alt={view.label} style={{ width:"100%", display:"block", objectFit:"cover" }} />
                      <div style={{ position:"absolute", bottom:10, left:10, backgroundColor:"rgba(255,255,255,0.88)", backdropFilter:"blur(6px)", padding:"3px 10px", borderRadius:999, fontSize:8, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", color:C }}>
                        {view.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Design details */}
            <div style={{ backgroundColor:"#fff", borderRadius:20, border:"1px solid #f0eeec", padding:"22px 24px", display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:"0.15em", color:"#a8a29e", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", marginBottom:4 }}>Design Specification</div>
              {[
                ["Occasion",   goalLabel],
                ["Silhouette", design.form.cut],
                ["Fabric",     fabricName],
                ["Colour",     design.form.colorDescription],
                ["Neckline",   design.form.neckline],
                ["Sleeves",    design.form.sleeves],
                ["Length",     design.form.dressLength],
                ["Lining",     design.form.lining],
              ].map(function(row) {
                return (
                  <div key={row[0]} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid #fafaf9" }}>
                    <span style={{ fontSize:11, color:"#78716c", fontFamily:"system-ui,sans-serif" }}>{row[0]}</span>
                    <span style={{ fontSize:12, fontWeight:600, color:C, fontFamily:"system-ui,sans-serif" }}>{row[1]}</span>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <button onClick={onStartDesign} style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, width:"100%", padding:"18px 0", backgroundColor:"#C9A0A0", color:"#2D2926", fontWeight:700, fontSize:11, letterSpacing:"0.35em", textTransform:"uppercase", borderRadius:14, border:"none", cursor:"pointer", boxShadow:"0 4px 20px rgba(45,41,38,0.22)", fontFamily:"system-ui,sans-serif" }}>
              <Sparkles style={{ width:14, height:14 }} /> Start My Own Design
            </button>

            <div style={{ textAlign:"center", fontSize:9, color:"#b8b0a8", letterSpacing:"0.15em", fontFamily:"system-ui,sans-serif" }}>
              © Tabitha 2026 · <a href="/privacy.html" style={{ color:"#b8b0a8", textDecoration:"underline" }}>Privacy Policy</a> · <a href="/terms.html" style={{ color:"#b8b0a8", textDecoration:"underline" }}>Terms &amp; Conditions</a> · <a href="/faq.html" style={{ color:"#b8b0a8", textDecoration:"underline" }}>FAQ</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const C = "#2D2926";
const NAV_BG = "#FFFFFF";
const NAV_BG_SOFT = "#EDE8E3";
const NAV_BORDER = "#E0D9D3";
const NAV_TEXT = "#2D2926";
const NAV_MUTED = "#9e8f88";

// ─────────────────────────────────────────────────────────────────────────────
// Shared initial state shapes — mirrors each component's own INITIAL
// ─────────────────────────────────────────────────────────────────────────────
const INITIAL_FORM = {
  category:            "gala",
  colorDescription:    "Dusty Rose",
  colorHex:            "#c9a0a0",
  fabricId:            "velvet",
  customFabric:        "",
  lining:              "Fully Lined",
  cut:                 "A-line",
  customCut:           "",
  neckline:            "V-Neck",
  customNeckline:      "",
  backNeckline:        "Low V-Back",
  customBackNeckline:  "",
  sleeves:             "Cap Sleeve",
  customSleeves:       "",
  closure:             "Invisible Zip — Side Seam",
  customClosure:       "",
  dressLength:         "Floor",
  customDressLength:   "",
  separates:           false,
  skirtType:           "A-line",
  customSkirtType:     "",
  skirtLength:         "Floor",
  customSkirtLength:   "",
  jacketNeckline:      "Notch Lapel",
  customJacketNeckline:"",
  jacketSleeves:       "Long Sleeve",
  customJacketSleeves: "",
  jacketFront:         "",
  jacketBack:          "",
  skirtFront:          "",
  skirtBack:           "",
  atmosphere:          "A grand marble ballroom with crystal chandeliers and live orchestral music.",
  frontDescription:    "",
  backDescription:     "",
};

const INITIAL_MEASUREMENTS = {
  height:"", bust:"", highBust:"", waist:"", hips:"", hollowToHem:"",
  shoulder:"", sleeveLength:"", bicep:"",
  frontWaist:"", backWaist:"", waistToHip:"",
  posture:"", fitPreference:"",
};

function makeCollectionItem(
  form,
  measurements,
  designRender,
  designRenderBack,
  fittingRoomRender,
  fittingRoomRenderBack,
  techPackRef,
  fabricYardage,
  costEstimate,
  consentGiven,
  fittingRoomAdjustments,
  designBrief,
  designSeed,  // NEW
  isLocked     // NEW
) {
  return {
    id:               Date.now().toString(),
    savedAt:          new Date().toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" }),
    form:             Object.assign({}, form),
    measurements:     Object.assign({}, measurements),
    modelRender:      designRender,
    modelRenderBack:  designRender,
    designRender:     designRender,
    designRenderBack: null,
    designSeed:       designSeed || null,  // NEW: Save seed
    isLocked:         !!isLocked,          // NEW: Save lock state
    fittingRoomRender:fittingRoomRender,
    fittingRoomRenderBack:fittingRoomRenderBack || fittingRoomRender || null,
    techPackRef:      techPackRef || null,
    fabricYardage:    fabricYardage || null,
    costEstimate:     costEstimate || null,
    consentGiven:     !!consentGiven,
    fittingRoomAdjustments: Array.isArray(fittingRoomAdjustments) ? fittingRoomAdjustments : [],
    designBrief:      designBrief || "",
    status:           techPackRef ? "The Drawing Room" : (fittingRoomRender || fittingRoomRenderBack) ? "The Salon" : "Design",
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Step indicator — compact, shown in Collection and transition screens
// ─────────────────────────────────────────────────────────────────────────────
const STEP_LABELS = ["Design Studio", "The Fitting Room", "The Salon", "The Drawing Room"];

function MiniStepBar({ current, onStepClick }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6, padding:"12px 0" }}>
      {STEP_LABELS.map(function(label, i) {
        const done    = i < current;
        const active  = i === current;
        const clickable = done;
        return (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div
              onClick={clickable ? function() { onStepClick(i); } : undefined}
              style={{ display:"flex", alignItems:"center", gap:5, cursor: clickable ? "pointer" : "default", opacity: clickable ? 1 : 1 }}
            >
              <div style={{ width:20, height:20, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", backgroundColor: active ? "#C9A0A0" : done ? "#C9A0A0" : "#E0D9D3", border: active ? "none" : done ? "none" : "1.5px solid #cdc5be", flexShrink:0, transition:"transform 0.1s ease", transform: clickable ? "scale(1)" : "scale(1)" }}>
                <span style={{ fontSize:9, fontWeight:700, color: active ? "#2D2926" : done ? "#2D2926" : NAV_MUTED, fontFamily:"system-ui,sans-serif" }}>{i + 1}</span>
              </div>
              <span style={{ fontSize:10, fontWeight: active ? 700 : 500, color: active ? "#2D2926" : done ? "#6b5e58" : NAV_MUTED, fontFamily:"system-ui,sans-serif", whiteSpace:"nowrap", textDecoration: clickable ? "underline" : "none", textDecorationColor:"#C9A0A0" }}>{label}</span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{ width:16, height:1, backgroundColor: done ? "#C9A0A0" : "#D8D0C8", flexShrink:0 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepJourneyBar({ current, onStepClick }) {
  return (
    <div className="no-print" style={{ position:"sticky", top:0, zIndex:60, backgroundColor:NAV_BG, borderBottom:"1px solid " + NAV_BORDER, boxShadow:"0 2px 10px rgba(0,0,0,0.07)" }}>
      <div style={{ maxWidth:720, margin:"0 auto", padding:"8px 20px", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <MiniStepBar current={current} onStepClick={onStepClick} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Collection card
// ─────────────────────────────────────────────────────────────────────────────
const STATUS_STYLE = {
  "Design":       { bg:"#f5f4f2", color:"#78716c" },
  "Fitting Room": { bg:"#fdf8f3", color:"#c8963c" },
  "The Fitting Room": { bg:"#fdf8f3", color:"#c8963c" },
  "The Salon":        { bg:"#fdf8f3", color:"#c8963c" },
  "The Drawing Room": { bg:"#f0f7f0", color:"#5a9a5a" },
};

// ─────────────────────────────────────────────────────────────────────────────
// Auth button — avatar when signed in, "Sign in" when not
// ─────────────────────────────────────────────────────────────────────────────
function AuthButton({ user, onSignIn, onSignOut }) {
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [showInput,  setShowInput]  = useState(false);
  const [email,      setEmail]      = useState("");
  const [sending,    setSending]    = useState(false);
  const [sent,       setSent]       = useState(false);
  const [error,      setError]      = useState(null);

  function handleSend() {
    if (!email.trim()) return;
    setSending(true);
    setError(null);
    onSignIn(email.trim()).then(function() {
      setSent(true);
      setSending(false);
    }).catch(function(err) {
      setError(err.message || "Something went wrong. Please try again.");
      setSending(false);
    });
  }

  if (!user) {
    if (sent) {
      return (
        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:999, border:"1px solid #D8D0C8", backgroundColor:"#fff", fontFamily:"system-ui,sans-serif" }}>
          <span style={{ fontSize:10, fontWeight:700, color:"#7aad7a", letterSpacing:"0.1em", textTransform:"uppercase" }}>Check your email ✓</span>
        </div>
      );
    }

    if (showInput) {
      return (
        <div style={{ display:"flex", alignItems:"center", gap:6, position:"relative" }}>
          <input
            autoFocus
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={function(e) { setEmail(e.target.value); setError(null); }}
            onKeyDown={function(e) { if (e.key === "Enter") handleSend(); if (e.key === "Escape") { setShowInput(false); setEmail(""); setError(null); } }}
            style={{ padding:"7px 12px", borderRadius:999, border: error ? "1px solid #c0604a" : "1px solid #D8D0C8", fontSize:11, fontFamily:"system-ui,sans-serif", outline:"none", width:180, color:"#2D2926" }}
          />
          <button
            onClick={handleSend}
            disabled={sending || !email.trim()}
            style={{ padding:"7px 14px", borderRadius:999, border:"none", backgroundColor:"#C9A0A0", color:"#2D2926", fontSize:10, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", cursor: sending ? "default" : "pointer", fontFamily:"system-ui,sans-serif", opacity: sending ? 0.6 : 1 }}
          >
            {sending ? "Sending…" : "Send link"}
          </button>
          {error && (
            <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, fontSize:10, color:"#c0604a", fontFamily:"system-ui,sans-serif", whiteSpace:"nowrap" }}>{error}</div>
          )}
        </div>
      );
    }

    return (
      <button
        onClick={function() { setShowInput(true); }}
        style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}
      >
        <span style={{ fontSize:9, fontWeight:700, color:"#2D2926", letterSpacing:"0.12em", textTransform:"uppercase" }}>Sign In</span>
      </button>
    );
  }

  const initials = (user.email || "U").slice(0,2).toUpperCase();

  return (
    <div style={{ position:"relative" }}>
      <button
        onClick={function() { setMenuOpen(function(o) { return !o; }); }}
        style={{ width:32, height:32, borderRadius:"50%", border:"2px solid #C9A0A0", overflow:"hidden", cursor:"pointer", backgroundColor:"#f5f4f2", padding:0, display:"flex", alignItems:"center", justifyContent:"center" }}
      >
        <span style={{ fontSize:11, fontWeight:700, color:"#2D2926", fontFamily:"system-ui,sans-serif" }}>{initials}</span>
      </button>
      {menuOpen && (
        <>
          <div onClick={function() { setMenuOpen(false); }} style={{ position:"fixed", inset:0, zIndex:90 }} />
          <div style={{ position:"absolute", right:0, top:40, zIndex:100, backgroundColor:"#fff", borderRadius:14, boxShadow:"0 8px 32px rgba(0,0,0,0.15)", border:"1px solid #e8e4e0", minWidth:200, overflow:"hidden" }}>
            <div style={{ padding:"14px 16px", borderBottom:"1px solid #f0eeec" }}>
              <div style={{ fontSize:10, color:"#a8a29e", fontFamily:"system-ui,sans-serif" }}>{user.email}</div>
            </div>
            <button
              onClick={function() { setMenuOpen(false); onSignOut(); }}
              style={{ width:"100%", padding:"12px 16px", textAlign:"left", border:"none", backgroundColor:"transparent", cursor:"pointer", fontSize:11, fontWeight:600, color:"#c0604a", fontFamily:"system-ui,sans-serif", letterSpacing:"0.05em" }}
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Share toast — shown after copying a link
// ─────────────────────────────────────────────────────────────────────────────
function SaveToast() {
  return (
    <div style={{
      position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)",
      zIndex:200, backgroundColor:"#2D2926", borderRadius:16,
      boxShadow:"0 8px 32px rgba(0,0,0,0.22)",
      padding:"14px 22px", display:"flex", alignItems:"center", gap:12,
      animation:"slideUp 0.25s ease",
    }}>
      <div style={{ width:24, height:24, borderRadius:"50%", backgroundColor:"#6aa36a", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <Check style={{ width:13, height:13, color:"#fff" }} />
      </div>
      <span style={{ fontSize:12, fontWeight:700, color:"#fff", fontFamily:"system-ui,sans-serif" }}>
        Saved to your collection
      </span>
    </div>
  );
}

function PurchaseToast() {
  return (
    <div style={{
      position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)",
      zIndex:200, backgroundColor:"#2D2926", borderRadius:16,
      boxShadow:"0 8px 32px rgba(0,0,0,0.22)",
      padding:"16px 24px", display:"flex", alignItems:"center", gap:14,
      animation:"slideUp 0.25s ease", maxWidth:400, width:"calc(100vw - 40px)",
    }}>
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateX(-50%) translateY(12px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
      <div style={{ width:28, height:28, borderRadius:"50%", backgroundColor:"#C9A0A0", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <Check style={{ width:14, height:14, color:"#fff" }} />
      </div>
      <div>
        <div style={{ fontSize:12, fontWeight:700, color:"#fff", fontFamily:"system-ui,sans-serif" }}>Payment confirmed</div>
        <div style={{ fontSize:11, color:"#a8a29e", fontFamily:"system-ui,sans-serif", marginTop:2 }}>Your Tech Pack is ready to download below.</div>
      </div>
    </div>
  );
}

function ShareToast({ url, onClose }) {
  const [copied, setCopied] = useState(true);

  function copyAgain() {
    navigator.clipboard.writeText(url).then(function() {
      setCopied(true);
    });
  }

  return (
    <div style={{
      position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)",
      zIndex:200, backgroundColor:C, borderRadius:16, boxShadow:"0 8px 32px rgba(0,0,0,0.22)",
      padding:"16px 20px", maxWidth:420, width:"calc(100vw - 40px)",
      display:"flex", flexDirection:"column", gap:12, animation:"slideUp 0.25s ease",
    }}>
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateX(-50%) translateY(12px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:24, height:24, borderRadius:"50%", backgroundColor:"#6aa36a", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <Check style={{ width:13, height:13, color:"#fff" }} />
          </div>
          <span style={{ fontSize:12, fontWeight:700, color:"#fff", fontFamily:"system-ui,sans-serif" }}>
            Share link copied!
          </span>
        </div>
        <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", padding:4, display:"flex" }}>
          <X style={{ width:14, height:14, color:"rgba(255,255,255,0.5)" }} />
        </button>
      </div>
      <div style={{ fontSize:11, color:"rgba(255,255,255,0.6)", fontFamily:"system-ui,sans-serif", lineHeight:1.5 }}>
        Paste this link into a message, email, or anywhere to share your design.
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:8, backgroundColor:"rgba(255,255,255,0.08)", borderRadius:10, padding:"8px 12px" }}>
        <span style={{ flex:1, fontSize:10, color:"rgba(255,255,255,0.7)", fontFamily:"monospace", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
          {url}
        </span>
        <button onClick={copyAgain} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px", borderRadius:8, backgroundColor:"#C9A0A0", border:"none", cursor:"pointer", flexShrink:0 }}>
          <Copy style={{ width:10, height:10, color:C }} />
          <span style={{ fontSize:9, fontWeight:700, color:C, letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>
            {copied ? "Copied" : "Copy"}
          </span>
        </button>
      </div>
    </div>
  );
}

function CollectionCard({ item, onOpen, onDelete, onShare }) {
  const goalLabel = { business:"Business", cocktail:"Cocktail", gala:"Gala", bridal:"Bridal" }[item.form.category] || item.form.category;
  const fabricName = item.form.fabricId ? item.form.fabricId.charAt(0).toUpperCase() + item.form.fabricId.slice(1).replace("-", " ") : "Custom";
  const sv = STATUS_STYLE[item.status] || STATUS_STYLE["Design"];

  return (
    <div style={{ backgroundColor:"#fff", borderRadius:20, border:"1px solid #f0eeec", overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.05)", display:"flex", flexDirection:"column" }}>

      {/* Image area */}
      <div style={{ position:"relative", backgroundColor:"#1a1714", height:220 }}>
        {item.modelRender ? (
          <img src={item.modelRender} alt="Design" style={{ width:"100%", height:"100%", objectFit:"cover", objectPosition:"top", display:"block" }} />
        ) : (
          <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", opacity:0.15 }}>
            <Sparkles style={{ width:32, height:32, color:"#fff" }} />
          </div>
        )}
        {item.fittingRoomRender && (
          <div style={{ position:"absolute", bottom:10, right:10, width:64, height:80, borderRadius:10, overflow:"hidden", border:"2px solid rgba(255,255,255,0.3)", boxShadow:"0 2px 8px rgba(0,0,0,0.3)" }}>
            <img src={item.fittingRoomRender} alt="Fitting" style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
          </div>
        )}
        <div style={{ position:"absolute", top:10, left:10, width:20, height:20, borderRadius:5, backgroundColor: item.form.colorHex || "#c9a0a0", border:"2px solid rgba(255,255,255,0.4)", boxShadow:"0 1px 4px rgba(0,0,0,0.2)" }} />
      </div>

      {/* Info */}
      <div style={{ padding:"16px 18px 18px", flex:1, display:"flex", flexDirection:"column", gap:10 }}>
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif", letterSpacing:"-0.01em", lineHeight:1.3 }}>
              {goalLabel} {item.form.separates ? "Separates" : (item.form.cut || "Design")}
            </div>
            <div style={{ fontSize:11, color:"#78716c", fontFamily:"system-ui,sans-serif", marginTop:3 }}>
              {fabricName} {item.form.colorDescription ? " · " + item.form.colorDescription : ""}
            </div>
          </div>
          <span style={{ padding:"3px 9px", borderRadius:999, backgroundColor:sv.bg, fontSize:9, fontWeight:700, color:sv.color, letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"system-ui,sans-serif", flexShrink:0 }}>
            {item.status}
          </span>
        </div>

        <div style={{ fontSize:10, color:"#c4bfba", fontFamily:"system-ui,sans-serif" }}>
          Saved {item.savedAt}
        </div>

        <button onClick={function() { onOpen(item); }} style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:7, width:"100%", padding:"10px 0", backgroundColor:C, color:"#fff", fontWeight:700, fontSize:10, letterSpacing:"0.2em", textTransform:"uppercase", borderRadius:10, border:"none", cursor:"pointer", fontFamily:"system-ui,sans-serif", marginTop:"auto" }}>
          Open Design <ChevronRight style={{ width:11, height:11 }} />
        </button>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          <button onClick={function() { onShare(item); }} style={{ padding:"8px 0", borderRadius:10, border:"1px solid #e7e5e4", backgroundColor:"#fafaf9", color:"#78716c", fontSize:9, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
            Share
          </button>
          <button onClick={function() { if (window.confirm("Delete this design?")) onDelete(item); }} style={{ padding:"8px 0", borderRadius:10, border:"1px solid #fde8e8", backgroundColor:"#fff5f5", color:"#c0604a", fontSize:9, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Collection view
// ─────────────────────────────────────────────────────────────────────────────
function CollectionView({ collection, loading, onNewDesign, onOpenItem, onDeleteItem, onShareItem, onOpenGallery, onOpenContact, user, onSignIn, onSignOut, previousScreen, onBackToDesign }) {
  return (
    <div style={{ margin:0, padding:0, minHeight:"100vh", backgroundColor:"#F7F4F1", fontFamily:"system-ui,sans-serif" }}>

      {/* Studio-style sticky dusty rose header */}
      <div style={{ margin:0, padding:"18px 28px", backgroundColor:"#C9A0A0", borderBottom:"1px solid #b88f8f", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:60 }}>
        <button onClick={function() { if (onNewDesign) onNewDesign(); }} style={{ display:"flex", alignItems:"center", gap:12, background:"none", border:"none", padding:0, cursor:"pointer" }}>
          <Layers style={{ color:C, width:24, height:24, flexShrink:0 }} />
          <div style={{ textAlign:"left" }}>
            <div style={{ margin:0, padding:0, fontSize:21, fontWeight:700, letterSpacing:"-0.02em", color:C, fontFamily:"system-ui,sans-serif" }}>Tabitha</div>
            <div style={{ margin:0, padding:0, fontSize:10, fontWeight:700, letterSpacing:"0.24em", color:"#6f555f", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>My Collection</div>
          </div>
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {onOpenGallery && (
            <button onClick={onOpenGallery} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
              Gallery
            </button>
          )}
          <button onClick={onNewDesign} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
            New Design
          </button>
          <AuthButton user={user} onSignIn={onSignIn} onSignOut={onSignOut} />
          <button style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"default", fontFamily:"system-ui,sans-serif", opacity:0.45 }}>
            My Collection
          </button>
          <button onClick={onOpenContact} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>
            Contact Us
          </button>
        </div>
      </div>

      <div style={{ margin:"0 auto", padding:"28px 20px 64px", maxWidth:720 }}>

        {loading ? (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:400 }}>
            <div style={{ fontSize:11, color:"#a8a29e", letterSpacing:"0.2em", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>Loading your collection…</div>
          </div>

        ) : collection.length === 0 ? (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:400, gap:16, textAlign:"center" }}>
            <div style={{ width:64, height:64, borderRadius:"50%", backgroundColor:"#f5f4f2", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <BookOpen style={{ width:28, height:28, color:"#c4bfba" }} />
            </div>
            <div>
              <div style={{ fontSize:18, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif", letterSpacing:"-0.02em", marginBottom:8 }}>Your Collection is empty</div>
              <div style={{ fontSize:13, color:"#78716c", fontFamily:"system-ui,sans-serif", lineHeight:1.6, maxWidth:320 }}>
                Every design you create lives here — from first sketch to finished Tech Pack.
              </div>
            </div>
            <button onClick={onNewDesign} style={{ display:"flex", alignItems:"center", gap:8, padding:"14px 28px", borderRadius:999, backgroundColor:C, border:"none", cursor:"pointer", fontFamily:"system-ui,sans-serif", boxShadow:"0 4px 20px rgba(45,41,38,0.22)", marginTop:8 }}>
              <Sparkles style={{ width:14, height:14, color:"#fff" }} />
              <span style={{ fontSize:11, fontWeight:700, color:"#fff", letterSpacing:"0.2em", textTransform:"uppercase" }}>Start Designing</span>
            </button>
          </div>

        ) : (
          <div>
            <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", marginBottom:24 }}>
              <div>
                <div style={{ fontSize:22, fontWeight:700, color:C, fontFamily:"system-ui,sans-serif", letterSpacing:"-0.02em" }}>My Collection</div>
                <div style={{ fontSize:11, color:"#a8a29e", fontFamily:"system-ui,sans-serif", marginTop:4 }}>
                  {collection.length} {collection.length === 1 ? "design" : "designs"}
                </div>
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px, 1fr))", gap:20 }}>
              {collection.map(function(item) {
                return (
                  <CollectionCard
                    key={item.id}
                    item={item}
                    onOpen={onOpenItem}
                    onDelete={onDeleteItem}
                    onShare={onShareItem}
                  />
                );
              })}
            </div>
          </div>
        )}

      </div>

      {/* Footer */}
      <div style={{ textAlign:"center", padding:"24px 0 32px", fontSize:9, color:"#b8b0a8", letterSpacing:"0.15em", fontFamily:"system-ui,sans-serif" }}>
        © Tabitha 2026 · <a href="/privacy.html" style={{ color:"#b8b0a8", textDecoration:"underline" }}>Privacy Policy</a> · <a href="/terms.html" style={{ color:"#b8b0a8", textDecoration:"underline" }}>Terms &amp; Conditions</a> · <a href="/faq.html" style={{ color:"#b8b0a8", textDecoration:"underline" }}>FAQ</a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main App — owns all shared state
// ─────────────────────────────────────────────────────────────────────────────
export default function HemAndHawApp() {

  // ── Navigation ──────────────────────────────────────────────────────────────
  const [screen, setScreen] = useState(function() {
    // Always open to the welcome/step1 screen on a fresh page load
    return "step1";
  });
  const [previousScreen, setPreviousScreen] = useState(null);

  const navigate = useCallback(function(s) {
    setPreviousScreen(function(prev) { return screen !== s ? screen : prev; });
    setScreen(s);
    // Preserve ?admin=true across navigation by keeping it in the URL
    const params = new URLSearchParams(window.location.search);
    const adminParam = params.get("admin") === "true" ? "?admin=true" : "";
    window.history.pushState({ screen: s }, "", window.location.pathname + adminParam);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [screen]);

  // Keep a ref to navigate so the auth useEffect ([] deps) always calls the
  // latest version — avoids stale-closure issues with the screen-dependent navigate.
  const navigateRef = useRef(navigate);
  useEffect(function() { navigateRef.current = navigate; }, [navigate]);

  // Handle browser back/forward buttons
  useEffect(function() {
    function handlePopState(e) {
      const target = (e.state && e.state.screen) || "step1";
      setPreviousScreen(screen);
      setScreen(target);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    window.addEventListener("popstate", handlePopState);
    // Seed the initial history entry so there's always somewhere to go back to
    if (!window.history.state || !window.history.state.screen) {
      window.history.replaceState({ screen: "step1" }, "", window.location.pathname + window.location.search);
    }
    return function() { window.removeEventListener("popstate", handlePopState); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // ── Auth — Google sign-in ────────────────────────────────────────────────────
  const [user,         setUser]         = useState(null);
  const [authLoading,  setAuthLoading]  = useState(true);
  const [showAuthMenu, setShowAuthMenu] = useState(false);

  useEffect(function() {
    // Do NOT call getCurrentUser() separately — it races with onAuthStateChange
    // and can set user=null after SIGNED_IN has already set it correctly.
    // onAuthStateChange fires INITIAL_SESSION on mount (with the restored session
    // if one exists), then SIGNED_IN after an OAuth redirect. That's all we need.

    const subscription = onAuthStateChange(function(event, u) {
      // INITIAL_SESSION fires on every mount — use it to hydrate user state
      // instead of a separate getCurrentUser() call.
      if (event === "INITIAL_SESSION") {
        setUser(u);
        setAuthLoading(false);
        // If already signed in, reload collection under their user_id
        if (u) {
          const sid = localStorage.getItem("hh_session_id");
          if (sid) {
            loadSessionDesigns(sid, u.id).then(function(designs) {
              setCollection(designs);
            }).catch(function(err) {
              console.error("Collection load on init:", err);
            });
          }
        }
        return;
      }

      setUser(u);
      setAuthLoading(false);

      if (event === "SIGNED_OUT") {
        setCollection([]);
        // Only navigate away from screens that require auth
        // Don't interrupt gallery or step1 browsing
        const currentScreen = window.history.state && window.history.state.screen;
        if (currentScreen && !["step1", "gallery"].includes(currentScreen)) {
          navigateRef.current("step1");
        }
      }

      if (event === "SIGNED_IN" && u) {
        // Check if we saved state before sign-in redirect (genuine new sign-in)
        const raw = localStorage.getItem("tabitha_pending_signin");
        if (raw) {
          try {
            const saved = JSON.parse(raw);
            // Only restore if saved within the last 10 minutes — ignore stale entries
            const age = Date.now() - (saved.savedAt || 0);
            localStorage.removeItem("tabitha_pending_signin");
            if (age < 10 * 60 * 1000) {
              if (saved.form)                    setForm(saved.form);
              if (saved.measurements)            setMeasurements(saved.measurements);
              if (saved.designSeed)              setDesignSeed(saved.designSeed);
              if (saved.designRender)            setDesignRender(saved.designRender);
              if (saved.fittingRoomRender)       setFittingRoomRender(saved.fittingRoomRender);
              if (saved.fittingRoomRenderBack)   setFittingRoomRenderBack(saved.fittingRoomRenderBack);
              if (saved.fittingRoomAdjustments)  setFittingRoomAdjustments(saved.fittingRoomAdjustments);
              if (saved.designBrief)             setDesignBrief(saved.designBrief);
              if (saved.fabricYardage)           setFabricYardage(saved.fabricYardage);
              if (saved.costEstimate)            setCostEstimate(saved.costEstimate);
              if (saved.consentGiven)            setConsentGiven(saved.consentGiven);
              if (saved.isLocked !== undefined)  setIsLocked(saved.isLocked);
              navigateRef.current(saved.screen === "step4" ? "step4" : "collection");
            }
          } catch (e) {
            localStorage.removeItem("tabitha_pending_signin");
          }
        } else {
          // No pending_signin = returning visitor or fresh sign-in with no saved state.
          // Stay on the current screen (welcome/step1) — don't auto-redirect to collection.
        }
        const sessionId = localStorage.getItem("hh_session_id");
        if (sessionId) {
          mergeSessionToUser(sessionId, u.id).then(function() {
            return loadSessionDesigns(sessionId, u.id);
          }).then(function(designs) {
            setCollection(designs);
          }).catch(function(err) {
            console.error("Collection reload failed:", err);
          });
        }
      }

      if (event === "TOKEN_REFRESHED" && u) {
        const sessionId = localStorage.getItem("hh_session_id");
        if (sessionId) {
          loadSessionDesigns(sessionId, u.id).then(function(designs) {
            setCollection(designs);
          }).catch(function(err) {
            console.error("Collection reload failed:", err);
          });
        }
      }
    });

    return function() { subscription.unsubscribe(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Share token — check URL on load ─────────────────────────────────────────
  const [shareToken, setShareToken] = useState(function() {
    const params = new URLSearchParams(window.location.search);
    return params.get("share") || null;
  });

  // ── Purchase success — restore state after Stripe redirect ───────────────────
  const [purchaseSuccessToast, setPurchaseSuccessToast] = useState(false);
  const [purchaseJustCompleted, setPurchaseJustCompleted] = useState(false);
  useEffect(function() {
    const params = new URLSearchParams(window.location.search);
    if (params.get("purchase_success") !== "techpack") return;
    // Clean the URL immediately
    window.history.replaceState({}, "", window.location.pathname);
    // Restore design state saved before Stripe redirect
    try {
      const raw = localStorage.getItem("tabitha_pending_purchase");
      if (raw) {
        const saved = JSON.parse(raw);
        localStorage.removeItem("tabitha_pending_purchase");
        if (saved.form)                setForm(saved.form);
        if (saved.measurements)        setMeasurements(saved.measurements);
        if (saved.designSeed)          setDesignSeed(saved.designSeed);
        if (saved.designRender)        setDesignRender(saved.designRender);
        if (saved.fittingRoomRender)   setFittingRoomRender(saved.fittingRoomRender);
        if (saved.fittingRoomRenderBack) setFittingRoomRenderBack(saved.fittingRoomRenderBack);
      }
    } catch (e) {
      console.error("Could not restore purchase state:", e);
    }
    setPurchaseSuccessToast(true);
    setPurchaseJustCompleted(true);
    localStorage.setItem("tabitha_purchase_completed", "true");
    setTimeout(function() { setPurchaseSuccessToast(false); }, 8000);
    setScreen("step4");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Share toast ──────────────────────────────────────────────────────────────
  const [shareToastUrl, setShareToastUrl] = useState(null);

  // ── Session ──────────────────────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState(null);
  const [loadingGallery, setLoadingGallery] = useState(true);

  useEffect(function() {
    getOrCreateSession().then(function(id) {
      setSessionId(id);
      // Collection will be loaded by the onAuthStateChange INITIAL_SESSION handler
      // once auth state is resolved. Load anonymously for now as a baseline.
      return loadSessionDesigns(id, null);
    }).then(function(designs) {
      setCollection(designs);
    }).catch(function(err) {
      console.error("Supabase init error:", err);
    }).finally(function() {
      setLoadingGallery(false);
    });
  }, []);

  // ── Shared design state ──────────────────────────────────────────────────────
  const [form,         setForm]         = useState(INITIAL_FORM);
  const [measurements, setMeasurements] = useState(INITIAL_MEASUREMENTS);
  const [designRender,         setDesignRender]        = useState(null);
  const [designRenderBack,     setDesignRenderBack]    = useState(null);
  const [designSeed,           setDesignSeed]          = useState(null);  // NEW: Seed for consistency
  const [isLocked,             setIsLocked]            = useState(false); // NEW: Lock state
  const [isGalleryFork,        setIsGalleryFork]       = useState(false); // Gallery fork — never pre-saved
  const [fabricYardage,        setFabricYardage]       = useState(null);
  const [costEstimate,         setCostEstimate]        = useState(null);
  const [consentGiven,         setConsentGiven]        = useState(false);
  const [fittingRoomRender,    setFittingRoomRender]   = useState(null);
  const [fittingRoomRenderBack, setFittingRoomRenderBack] = useState(null);
  const [fittingRoomAdjustments, setFittingRoomAdjustments] = useState([]);
  const [designBrief,         setDesignBrief]        = useState("");
  const [, setActiveTechPackRef]  = useState(null);

  // ── Collection ───────────────────────────────────────────────────────────────
  const [collection, setCollection] = useState([]);

  // ── Studio instance key — forces remount when opening a saved design ────────
  const [studioKey, setStudioKey] = useState(0);
  const skipWelcomeRef = useRef(false);

  // Save design state to localStorage before magic link sign-in
  // Returns a Promise so the AuthButton can show sent/error state
  const saveStateBeforeSignIn = useCallback(function(email) {
    try {
      localStorage.setItem("tabitha_pending_signin", JSON.stringify({
        screen, form, measurements, designSeed, designRender,
        fittingRoomRender, fittingRoomRenderBack, fittingRoomAdjustments,
        designBrief, fabricYardage, costEstimate, consentGiven, isLocked,
        savedAt: Date.now(),
      }));
    } catch (e) {
      try {
        localStorage.setItem("tabitha_pending_signin", JSON.stringify({
          screen, form, measurements, designSeed, designBrief,
          fabricYardage, costEstimate, consentGiven, isLocked,
          fittingRoomAdjustments, savedAt: Date.now(),
        }));
      } catch (_) {}
    }
    return signInWithMagicLink(email);
  }, [screen, form, measurements, designSeed, designRender,
      fittingRoomRender, fittingRoomRenderBack, fittingRoomAdjustments,
      designBrief, fabricYardage, costEstimate, consentGiven, isLocked]);

  // ── Navigation handlers ──────────────────────────────────────────────────────
  const startNewDesign = useCallback(function() {
    setForm(INITIAL_FORM);
    setMeasurements(INITIAL_MEASUREMENTS);
    setDesignRender(null);
    setDesignSeed(null);
    setIsLocked(false);
    setIsGalleryFork(false);
    setFabricYardage(null);
    setCostEstimate(null);
    setConsentGiven(false);
    setFittingRoomRender(null);
    setFittingRoomRenderBack(null);
    setFittingRoomAdjustments([]);
    setDesignBrief("");
    setActiveTechPackRef(null);
    skipWelcomeRef.current = true;        // Always skip welcome on New Design
    setStudioKey(function(k) { return k + 1; });
    navigate("step1");
  }, [navigate]);

  const openCollectionItem = useCallback(function(item) {
    setForm(item.form);
    setMeasurements(item.measurements);
    setDesignRender(item.designRender || null);
    setDesignSeed(item.designSeed || null);       // NEW: Load seed
    setIsLocked(item.isLocked || false);          // NEW: Load lock state
    setIsGalleryFork(false);
    setStudioKey(function(k) { return k + 1; });   // Force Studio remount
    setFabricYardage(item.fabricYardage || null);
    setCostEstimate(item.costEstimate || null);
    setConsentGiven(!!item.consentGiven);
    setFittingRoomRender(item.fittingRoomRender);
    setFittingRoomRenderBack(item.fittingRoomRenderBack || null);
    setFittingRoomAdjustments(item.fittingRoomAdjustments || []);
    setDesignBrief(item.designBrief || "");
    setActiveTechPackRef(item.techPackRef);
    // Route to furthest completed step
    if (item.techPackRef)             navigate("step4");
    else if (item.fittingRoomRender || item.fittingRoomRenderBack)  navigate("step3");
    else if (Object.keys(item.measurements || {}).some(function(key) { return !!item.measurements[key]; })) navigate("step2");
    else                              navigate("step1");
  }, [navigate]);

  const handleLoadBySeed = useCallback(async function(seed) {
    // 1. Check in-memory collection first (instant)
    const match = collection.find(function(item) {
      return String(item.designSeed) === String(seed);
    });
    if (match) {
      openCollectionItem(match);
      return true;
    }
    // 2. Fall back to a direct Supabase lookup (catches designs from other
    //    sessions, other browsers, or pre-sign-in saves)
    try {
      const found = await loadDesignBySeed(seed);
      if (found) {
        openCollectionItem(found);
        return true;
      }
    } catch (err) {
      console.error("Seed lookup failed:", err);
    }
    return false;
  }, [collection, openCollectionItem]);

  // ── Studio data + navigation ────────────────────────────────────────────────
  const handleStudioDataChange = useCallback(function(data) {
    if (data?.form) setForm(data.form);
    if (data && Object.prototype.hasOwnProperty.call(data, "designRender")) {
      setDesignRender(data.designRender);
    }
    if (data && Object.prototype.hasOwnProperty.call(data, "designSeed")) {
      setDesignSeed(data.designSeed || null);
    }
    if (data && Object.prototype.hasOwnProperty.call(data, "isLocked")) {
      setIsLocked(!!data.isLocked);
    }
  }, []);

  const handleDesignComplete = useCallback(function(payload) {
    if (payload?.form) setForm(payload.form);
    if (payload && Object.prototype.hasOwnProperty.call(payload, "designRender")) {
      setDesignRender(payload.designRender);
    }
    if (payload && Object.prototype.hasOwnProperty.call(payload, "designSeed")) {
      setDesignSeed(payload.designSeed);  // NEW: Save seed from Studio
    }
    if (payload && Object.prototype.hasOwnProperty.call(payload, "isLocked")) {
      setIsLocked(payload.isLocked);      // NEW: Save lock state from Studio
    }
    navigate("step2");
  }, [navigate]);

  // ── Fitting Room (measurements) data + navigation ────────────────────────────
  const handleWorkroomDataChange = useCallback(function(data) {
    if (data?.measurements) setMeasurements(data.measurements);
    if (data && Object.prototype.hasOwnProperty.call(data, "fabricYardage")) {
      setFabricYardage(data.fabricYardage);
    }
    if (data && Object.prototype.hasOwnProperty.call(data, "costEstimate")) {
      setCostEstimate(data.costEstimate);
    }
    if (data && Object.prototype.hasOwnProperty.call(data, "consentGiven")) {
      setConsentGiven(!!data.consentGiven);
    }
  }, []);

  const handleMeasurementsComplete = useCallback(function(data) {
    if (data?.measurements) setMeasurements(data.measurements);
    if (data && Object.prototype.hasOwnProperty.call(data, "fabricYardage")) {
      setFabricYardage(data.fabricYardage);
    }
    if (data && Object.prototype.hasOwnProperty.call(data, "costEstimate")) {
      setCostEstimate(data.costEstimate);
    }
    if (data && Object.prototype.hasOwnProperty.call(data, "consentGiven")) {
      setConsentGiven(!!data.consentGiven);
    }
    navigate("step3");
  }, [navigate]);

  // ── Salon (AI render + chat) data + navigation ───────────────────────────────
  const handleFittingRoomDataChange = useCallback(function(data) {
    if (data && Object.prototype.hasOwnProperty.call(data, "fittingRoomRender")) {
      setFittingRoomRender(data.fittingRoomRender || null);
    }
    if (data && Object.prototype.hasOwnProperty.call(data, "fittingRoomRenderBack")) {
      setFittingRoomRenderBack(data.fittingRoomRenderBack || null);
    }
    if (Array.isArray(data?.fittingRoomAdjustments)) {
      setFittingRoomAdjustments(data.fittingRoomAdjustments);
    }
    if (data && Object.prototype.hasOwnProperty.call(data, "designBrief")) {
      setDesignBrief(data.designBrief || "");
    }
  }, []);

  const handleFittingRoomComplete = useCallback(function(data) {
    const newFrontRender = (data && Object.prototype.hasOwnProperty.call(data, "fittingRoomRender"))
      ? (data.fittingRoomRender || null) : fittingRoomRender;
    const newBackRender = (data && Object.prototype.hasOwnProperty.call(data, "fittingRoomRenderBack"))
      ? (data.fittingRoomRenderBack || null) : fittingRoomRenderBack;

    if (newFrontRender !== fittingRoomRender) setFittingRoomRender(newFrontRender);
    if (newBackRender  !== fittingRoomRenderBack) setFittingRoomRenderBack(newBackRender);
    if (Array.isArray(data?.fittingRoomAdjustments)) {
      setFittingRoomAdjustments(data.fittingRoomAdjustments);
    }
    if (data && Object.prototype.hasOwnProperty.call(data, "designBrief")) {
      setDesignBrief(data.designBrief || "");
    }
    navigate("step4");
  }, [fittingRoomRender, fittingRoomRenderBack, navigate]);

  // ── Save to Collection at any step (e.g. "Save to Collection" button) ─────────
  const [saveToast, setSaveToast] = useState(false);

  const handleSaveToCollection = useCallback(async function(payload) {
    const techPackRef = screen === "step4" ? ("TP-" + Date.now()) : null;
    if (techPackRef) setActiveTechPackRef(techPackRef);

    // Prefer values from the payload (passed directly from Studio/other screens)
    // over closure state, which may be one render behind due to async setState.
    const saveForm          = (payload && payload.form)          || form;
    const saveDesignRender  = (payload && payload.designRender  != null) ? payload.designRender  : designRender;
    const saveDesignSeed    = (payload && payload.designSeed    != null) ? payload.designSeed    : designSeed;
    const saveIsLocked      = (payload && payload.isLocked      != null) ? payload.isLocked      : isLocked;

    // Sync App state from payload so subsequent operations see fresh values
    if (payload && payload.form)           setForm(payload.form);
    if (payload && payload.designRender   != null) setDesignRender(payload.designRender);
    if (payload && payload.designSeed     != null) setDesignSeed(payload.designSeed);
    if (payload && payload.isLocked       != null) setIsLocked(!!payload.isLocked);

    // Build local item for immediate UI update
    const item = makeCollectionItem(
      saveForm, measurements, saveDesignRender, null,
      fittingRoomRender, fittingRoomRenderBack, techPackRef,
      fabricYardage, costEstimate, consentGiven,
      fittingRoomAdjustments, designBrief, saveDesignSeed, saveIsLocked
    );
    setCollection(function(prev) { return [item, ...prev]; });

    // Show success toast — stay on current screen
    setSaveToast(true);
    setTimeout(function() { setSaveToast(false); }, 2500);

    // Persist to Supabase in background
    if (sessionId) {
      try {
        const dbId = await saveDesign({
          sessionId,
          userId: user ? user.id : null,
          form:          saveForm,
          measurements,
          consentGiven,
          designRender:  saveDesignRender,
          fittingRoomRender,
          fittingRoomRenderBack,
          designSeed:    saveDesignSeed,
          designBrief,
          fabricYardage,
          costEstimate,
          fittingRoomAdjustments,
          status: item.status,
        });
        setCollection(function(prev) {
          return prev.map(function(d) {
            return d.id === item.id ? Object.assign({}, d, { dbId }) : d;
          });
        });
      } catch (err) {
        console.error("Failed to save to Supabase:", err);
      }
    }
  }, [screen, form, measurements, designRender, fittingRoomRender,
      fittingRoomRenderBack, fabricYardage, costEstimate, consentGiven,
      fittingRoomAdjustments, designBrief, designSeed, isLocked, sessionId, user]);

  // ── Delete a design from the gallery ────────────────────────────────────────
  const handleDeleteDesign = useCallback(async function(item) {
    setCollection(function(prev) { return prev.filter(function(d) { return d.id !== item.id; }); });
    if (item.dbId) {
      try { await deleteDesign(item.dbId); } catch (err) { console.error("Delete failed:", err); }
    }
  }, []);

  // ── Submit a saved design to the gallery for review ─────────────────────────
  const handleSubmitToGallery = useCallback(async function({ form: _f, designRender: _r, designSeed: _s }) {
    // The design must already be saved to Supabase — find its dbId in the collection
    const saved = collection.find(function(d) { return d.designSeed === _s; });
    if (!saved?.dbId) throw new Error("Design not yet saved to database — save first.");
    await submitDesignToGallery(saved.dbId);
  }, [collection]);

  // ── Admin flag — derived from ?admin=true URL param and a known admin email ──
  const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "").split(",").map(function(e) { return e.trim().toLowerCase(); });
  const isAdmin = !!(user && ADMIN_EMAILS.length && ADMIN_EMAILS.includes((user.email || "").toLowerCase()))
    || new URLSearchParams(window.location.search).get("admin") === "true";


  const handleShareDesign = useCallback(async function(item) {
    let dbId = item.dbId;

    // No dbId means the design hasn't been saved to Supabase yet
    if (!dbId) {
      if (!user) {
        alert("Please sign in first — sharing requires a saved account.");
        return;
      }
      try {
        dbId = await saveDesign({
          sessionId,
          userId: user.id,
          form:                  item.form,
          measurements:          item.measurements,
          consentGiven:          item.consentGiven || false,
          designRender:          item.designRender,
          fittingRoomRender:     item.fittingRoomRender,
          fittingRoomRenderBack: item.fittingRoomRenderBack,
          designSeed:            item.designSeed,
          designBrief:           item.designBrief,
          fabricYardage:         item.fabricYardage,
          costEstimate:          item.costEstimate,
          fittingRoomAdjustments:item.fittingRoomAdjustments || [],
          status:                item.status,
        });
        // Update collection so future shares don't re-save
        setCollection(function(prev) {
          return prev.map(function(d) {
            return d.id === item.id ? Object.assign({}, d, { dbId }) : d;
          });
        });
      } catch (err) {
        console.error("Auto-save for share failed:", err);
        alert("Could not save your design — please try again.");
        return;
      }
    }

    try {
      const token = await createShareToken(dbId);
      const url   = window.location.origin + "?share=" + token;
      await navigator.clipboard.writeText(url);
      setShareToastUrl(url);
      setTimeout(function() { setShareToastUrl(null); }, 8000);
    } catch (err) {
      console.error("Share failed:", err);
      alert("Could not create share link — please try again.");
    }
  }, [user, sessionId, setCollection]);

  // ── Start with a gallery design — forks cleanly into Studio ────────────────
  const handleStartWithGalleryDesign = useCallback(function(item) {
    // Pre-fill Studio with the gallery design's form + render, but
    // treat it as a fresh fork: no lock, no saved state, new seed on Generate.
    setForm(item.form || INITIAL_FORM);
    setMeasurements(INITIAL_MEASUREMENTS);
    setDesignRender(item.designRender || null);
    setDesignSeed(item.designSeed || null);
    setIsLocked(false);   // user must generate a new render to get their own seed
    setIsGalleryFork(true);  // mark as gallery fork — Studio must not treat as saved
    setFabricYardage(null);
    setCostEstimate(null);
    setConsentGiven(false);
    setFittingRoomRender(null);
    setFittingRoomRenderBack(null);
    setFittingRoomAdjustments([]);
    setDesignBrief("");
    setActiveTechPackRef(null);
    setStudioKey(function(k) { return k + 1; });
    navigate("step1");
  }, [navigate]);

  // ── Step navigation (used by clickable MiniStepBar) ─────────────────────────
  const goToStep = useCallback(function(i) {
    navigate(["step1","step2","step3","step4"][i]);
  }, [navigate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  if (shareToken) {
    return (
      <>
        <SharedDesignView
          token={shareToken}
          onStartDesign={function() {
            // Clear the share token from URL and go to new design
            window.history.replaceState({}, "", window.location.pathname);
            setShareToken(null);
            startNewDesign();
          }}
        />
        <TabithaChat avatarSrc={tabithaAvatar} apiKey={ANTHROPIC_KEY} />
      </>
    );
  }

  if (screen === "gallery") {
    return (
      <>
        <TheGallery
          onStartWithDesign={handleStartWithGalleryDesign}
          onNewDesign={startNewDesign}
          onNavigate={navigate}
          user={user}
          onSignIn={saveStateBeforeSignIn}
          onSignOut={signOut}
          isAdmin={isAdmin}
          // Admin props — passed through so the admin panel can publish designs
          currentForm={form}
          currentDesignRender={designRender}
          currentDesignSeed={designSeed}
        />
        <TabithaChat avatarSrc={tabithaAvatar} apiKey={ANTHROPIC_KEY} />
      </>
    );
  }

  if (screen === "collection") {
    return (
      <>
        <CollectionView
          collection={collection}
          loading={loadingGallery}
          onNewDesign={startNewDesign}
          onOpenItem={openCollectionItem}
          onDeleteItem={handleDeleteDesign}
          onShareItem={handleShareDesign}
          onOpenGallery={function() { navigate("gallery"); }}
          onOpenContact={function() { navigate("contact"); }}
          user={user}
          onSignIn={saveStateBeforeSignIn}
          onSignOut={signOut}
          previousScreen={previousScreen}
          onBackToDesign={function() { navigate(previousScreen || "step1"); }}
        />
        {shareToastUrl && (
          <ShareToast
            url={shareToastUrl}
            onClose={function() { setShareToastUrl(null); }}
          />
        )}
        <TabithaChat avatarSrc={tabithaAvatar} apiKey={ANTHROPIC_KEY} />
      </>
    );
  }

  if (screen === "contact") {
    return (
      <>
        <ContactPage
          onNavigate={navigate}
          user={user}
          onSignIn={saveStateBeforeSignIn}
          onSignOut={signOut}
        />
        <TabithaChat avatarSrc={tabithaAvatar} apiKey={ANTHROPIC_KEY} />
      </>
    );
  }

  if (screen === "step1") {
    return (
      <>
        {/* Dusty rose brand header — sits above journey bar on Design Studio */}
        <div style={{ margin:0, padding:"18px 28px", backgroundColor:"#C9A0A0", borderBottom:"1px solid #b88f8f", display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:61 }}>
          <button onClick={function() { navigate("step1"); }} style={{ display:"flex", alignItems:"center", gap:12, background:"none", border:"none", padding:0, cursor:"pointer" }}>
            <Layers style={{ color:C, width:24, height:24, flexShrink:0 }} />
            <div style={{ textAlign:"left" }}>
              <div style={{ margin:0, padding:0, fontSize:21, fontWeight:700, letterSpacing:"-0.02em", color:C, fontFamily:"system-ui,sans-serif" }}>Tabitha</div>
              <div style={{ margin:0, padding:0, fontSize:10, fontWeight:700, letterSpacing:"0.24em", color:"#6f555f", textTransform:"uppercase", fontFamily:"system-ui,sans-serif" }}>Design Studio</div>
            </div>
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button onClick={function() { navigate("gallery"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>Gallery</button>
            <button style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"default", fontFamily:"system-ui,sans-serif", opacity:0.45 }}>New Design</button>
            <AuthButton user={user} onSignIn={saveStateBeforeSignIn} onSignOut={signOut} />
            <button onClick={function() { navigate("collection"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>My Collection</button>
            <button onClick={function() { navigate("contact"); }} style={{ padding:"7px 12px", borderRadius:999, border:"1px solid rgba(255,255,255,0.5)", backgroundColor:"#fff", fontSize:9, fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:"#2D2926", cursor:"pointer", fontFamily:"system-ui,sans-serif" }}>Contact Us</button>
          </div>
        </div>
        <StepJourneyBar current={0} onStepClick={goToStep} />
        <Studio
          key={studioKey}
          initialForm={form}
          initialDesignRender={designRender}
          initialDesignSeed={designSeed}
          initialIsLocked={isLocked}
          initialIsSaved={isGalleryFork ? false : null}
          skipWelcome={!!designRender || ["gallery","collection","contact"].includes(previousScreen) || skipWelcomeRef.current}
          hideHeader={true}
          onStudioDataChange={function(data) { skipWelcomeRef.current = false; handleStudioDataChange(data); }}
          onProceedToFittingRoom={handleDesignComplete}
          onBackToCollection={function() { navigate("collection"); }}
          onSaveToCollection={handleSaveToCollection}
          onSubmitToGallery={handleSubmitToGallery}
          isSignedIn={!!user}
          onSignIn={saveStateBeforeSignIn}
          onLoadBySeed={handleLoadBySeed}
        />
        <TabithaChat avatarSrc={tabithaAvatar} apiKey={ANTHROPIC_KEY} />
      </>
    );
  }

  if (screen === "step2") {
    return (
      <>
        <StepJourneyBar current={1} onStepClick={goToStep} />
        <Measurements
          form={form}
          designRender={designRender}
          initialMeasurements={measurements}
          silhouette={form.cut}
          fabricId={form.fabricId}
          designGoal={form.category}
          lining={form.lining}
          onMeasurementsDataChange={handleWorkroomDataChange}
          onProceedToSalon={handleMeasurementsComplete}
          onReturnToStudio={function() { navigate("step1"); }}
        />
        <TabithaChat avatarSrc={tabithaAvatar} apiKey={ANTHROPIC_KEY} />
      </>
    );
  }

  if (screen === "step3") {
    return (
      <>
        <StepJourneyBar current={2} onStepClick={goToStep} />
        <Salon
          form={form}
          measurements={measurements}
          fabricYardage={fabricYardage}
          costEstimate={costEstimate}
          designRender={designRender}
          designRenderBack={null}
          designSeed={designSeed}  // NEW: Pass seed for consistent renders
          initialFittingRoomRender={fittingRoomRender}
          initialFittingRoomRenderBack={fittingRoomRenderBack}
          initialFittingRoomAdjustments={fittingRoomAdjustments}
          initialDesignBrief={designBrief}
          onFittingRoomDataChange={handleFittingRoomDataChange}
          onReturnToFittingRoom={function() { navigate("step2"); }}
          onReturnToStudio={function() { navigate("step1"); }}
          onProceedToDrawingRoom={handleFittingRoomComplete}
        />
        <TabithaChat avatarSrc={tabithaAvatar} apiKey={ANTHROPIC_KEY} />
      </>
    );
  }

  if (screen === "step4") {
    return (
      <>
        <TechPack
          form={form}
          measurements={measurements}
          designRender={designRender}
          modelRender={designRender}
          designRenderBack={null}
          fittingRoomRender={fittingRoomRender}
          fittingRoomRenderBack={fittingRoomRenderBack}
          fittingRoomAdjustments={fittingRoomAdjustments}
          designBrief={designBrief}
          fabricYardage={fabricYardage}
          costEstimate={costEstimate}
          consentGiven={consentGiven}
          onReturnToSalon={function() { navigate("step3"); }}
          user={user}
          onSignIn={saveStateBeforeSignIn}
          onSignOut={signOut}
          onNavigate={navigate}
          onGoHome={function() { skipWelcomeRef.current = true; navigate("step1"); }}
          purchaseJustCompleted={purchaseJustCompleted}
          renderJourneyBar={function() {
            return <StepJourneyBar current={3} onStepClick={goToStep} />;
          }}
        />
        <TabithaChat avatarSrc={tabithaAvatar} apiKey={ANTHROPIC_KEY} />
      </>
    );
  }


  return (
    <>
      {saveToast && <SaveToast />}
      {purchaseSuccessToast && <PurchaseToast />}
      <TabithaChat avatarSrc={tabithaAvatar} apiKey={ANTHROPIC_KEY} />
    </>
  );
}
